/**
 * Tab Background Story
 * Tab 切换：文案瞬间切换；产品图按方向做 3D 交叉过渡。
 * 下一张：左出右进；上一张：右出左进。
 * 同一时间只跑一轮切换，避免连点造成图层残留。
 */
(function () {
  'use strict';

  var SELECTOR = '[data-tab-story]';
  var MOBILE_QUERY = '(max-width: 768px)';

  /** 用户开启「减弱动态效果」时跳过滑动动画。 */
  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * 初始化单个 section：收集 Tab / 面板 / 产品图层，绑定事件，并预加载当前图。
   * @param {HTMLElement} root data-tab-story 根节点
   */
  function TabBackgroundStory(root) {
    this.root = root;
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-tab-target]'));
    this.panels = Array.prototype.slice.call(root.querySelectorAll('[data-panel]'));
    this.layers = Array.prototype.slice.call(root.querySelectorAll('[data-media-layer]'));
    this.animate = root.getAttribute('data-animate') !== 'false';
    this.duration = parseInt(root.getAttribute('data-duration'), 10) || 520;
    this.transitioning = false;
    this.pendingId = null;
    this.bound = {};
    this.activeId = this.getActiveId();

    if (!this.tabs.length) return;

    this.bind();
    this.preloadLayer(this.getLayer(this.activeId), true);
  }

  /** 读取当前激活 Tab 的 block id；没有激活态时回退到第一个 Tab。 */
  TabBackgroundStory.prototype.getActiveId = function () {
    var activeTab = this.root.querySelector('[data-tab-target].is-active');
    if (activeTab) return activeTab.getAttribute('data-tab-target');
    return this.tabs[0] ? this.tabs[0].getAttribute('data-tab-target') : '';
  };

  /** 按 block id 找到对应的产品图层。 */
  TabBackgroundStory.prototype.getLayer = function (id) {
    if (!id) return null;
    for (var i = 0; i < this.layers.length; i += 1) {
      if (this.layers[i].getAttribute('data-media-layer') === id) {
        return this.layers[i];
      }
    }
    return null;
  };

  /** 按当前视口返回该图层应使用的 PC 或移动端产品图 URL。 */
  TabBackgroundStory.prototype.getImageUrl = function (layer) {
    if (!layer) return '';
    var isMobile = window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
    if (isMobile) {
      return layer.getAttribute('data-mobile-image') || layer.getAttribute('data-desktop-image') || '';
    }
    return layer.getAttribute('data-desktop-image') || '';
  };

  /**
   * 预加载产品图。加载完成后再开启动画，避免切图过程露出空白。
   * @param {HTMLElement} layer
   * @param {boolean} highPriority 首屏当前层使用高优先级拉取
   * @returns {Promise<boolean>} 成功 true，失败/无图也 true（占位可直接切）
   */
  TabBackgroundStory.prototype.preloadLayer = function (layer, highPriority) {
    var url = this.getImageUrl(layer);
    if (!url) return Promise.resolve(true);

    return new Promise(function (resolve) {
      var image = new Image();
      if (highPriority && 'fetchPriority' in image) {
        image.fetchPriority = 'high';
      }
      image.onload = function () {
        resolve(true);
      };
      image.onerror = function () {
        console.warn('[tab-background-story] failed to preload product image', url);
        resolve(false);
      };
      image.src = url;
    });
  };

  /**
   * 等待产品层 CSS 过渡结束；若 transitionend 未触发，则用 duration + 缓冲超时兜底。
   * @param {HTMLElement} el
   */
  TabBackgroundStory.prototype.waitForTransition = function (el) {
    var fallback = this.duration + 120;
    return new Promise(function (resolve) {
      var done = false;
      var timer = window.setTimeout(finish, fallback);

      function finish() {
        if (done) return;
        done = true;
        el.removeEventListener('transitionend', onEnd);
        window.clearTimeout(timer);
        resolve();
      }

      function onEnd(event) {
        if (event.target !== el) return;
        if (event.propertyName === 'transform' || event.propertyName === 'opacity') {
          finish();
        }
      }

      el.addEventListener('transitionend', onEnd);
    });
  };

  /**
   * 同时等待离场/入场两层过渡（透明 PNG 交叉时两边都要跑完）。
   * @param {HTMLElement|null} leaving
   * @param {HTMLElement} entering
   */
  TabBackgroundStory.prototype.waitForCrossfade = function (leaving, entering) {
    var waits = [this.waitForTransition(entering)];
    if (leaving && leaving !== entering) {
      waits.push(this.waitForTransition(leaving));
    }
    return Promise.all(waits);
  };

  /** 同步 Tab 激活态、文案面板显隐，以及 aria 属性（文案无动画）。 */
  TabBackgroundStory.prototype.syncTabsAndPanels = function (nextId) {
    this.tabs.forEach(function (tab) {
      var selected = tab.getAttribute('data-tab-target') === nextId;
      tab.classList.toggle('is-active', selected);
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
    });

    this.panels.forEach(function (panel) {
      var selected = panel.getAttribute('data-panel') === nextId;
      panel.classList.toggle('is-active', selected);
      panel.setAttribute('aria-hidden', selected ? 'false' : 'true');
    });
  };

  /** 清掉图层上的入场/离场/激活 class，避免状态残留。 */
  TabBackgroundStory.prototype.resetLayerState = function (layer) {
    if (!layer) return;
    layer.classList.remove('is-incoming', 'is-entering', 'is-leaving', 'is-active');
  };

  /** 按 block id 返回 Tab 下标，找不到则 -1。 */
  TabBackgroundStory.prototype.getIdIndex = function (id) {
    if (!id) return -1;
    for (var i = 0; i < this.tabs.length; i += 1) {
      if (this.tabs[i].getAttribute('data-tab-target') === id) return i;
    }
    return -1;
  };

  /**
   * 根据目标 Tab 相对当前 Tab 的位置，决定动画方向。
   * @param {string} nextId
   * @returns {'next'|'prev'}
   */
  TabBackgroundStory.prototype.getMediaDirection = function (nextId) {
    var fromIndex = this.getIdIndex(this.activeId);
    var toIndex = this.getIdIndex(nextId);
    if (fromIndex < 0 || toIndex < 0) return 'next';
    return toIndex < fromIndex ? 'prev' : 'next';
  };

  /**
   * 切换到指定 Tab：先同步文案，再按方向交叉过渡产品图。
   * next：当前图左出，下一张右进；prev：当前图右出，下一张左进。
   * 动画进行中再次点击会记下 pendingId，等当前轮结束后再切。
   * @param {string} nextId
   * @param {{ instant?: boolean }} [options] instant 为 true 时（如 Theme Editor 选中 block）立刻切换
   */
  TabBackgroundStory.prototype.selectTab = function (nextId, options) {
    var self = this;
    var instant = options && options.instant;
    if (!nextId || nextId === this.activeId) return Promise.resolve();

    if (this.transitioning) {
      this.pendingId = nextId;
      return Promise.resolve();
    }

    var currentLayer = this.getLayer(this.activeId);
    var nextLayer = this.getLayer(nextId);
    if (!nextLayer) return Promise.resolve();

    var mediaDir = this.getMediaDirection(nextId);

    this.transitioning = true;
    this.root.setAttribute('data-transitioning', 'true');
    this.root.setAttribute('data-media-dir', mediaDir);
    this.pendingId = null;
    this.syncTabsAndPanels(nextId);

    var shouldAnimate = this.animate && !prefersReducedMotion() && !instant;

    return this.preloadLayer(nextLayer, false).then(function (loaded) {
      if (!shouldAnimate || !loaded) {
        self.resetLayerState(currentLayer);
        self.resetLayerState(nextLayer);
        nextLayer.classList.add('is-active');
        self.activeId = nextId;
        self.finishTransition();
        return;
      }

      // 入场层先落到方向对应的后方起点，再在下一帧过渡到正面。
      nextLayer.classList.remove('is-active', 'is-leaving', 'is-entering');
      nextLayer.classList.add('is-incoming');

      // 强制回流，确保离场从当前正面状态开始算 transition。
      if (currentLayer && currentLayer !== nextLayer) {
        void currentLayer.offsetWidth;
      }
      void nextLayer.offsetWidth;

      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          if (currentLayer && currentLayer !== nextLayer) {
            currentLayer.classList.add('is-leaving');
          }
          nextLayer.classList.add('is-entering');
        });
      });

      return self.waitForCrossfade(currentLayer, nextLayer).then(function () {
        if (currentLayer && currentLayer !== nextLayer) {
          currentLayer.classList.remove('is-active', 'is-leaving');
        }
        nextLayer.classList.remove('is-incoming', 'is-entering');
        nextLayer.classList.add('is-active');
        self.activeId = nextId;
        self.finishTransition();
      });
    });
  };

  /** 解锁切换锁；若动画期间又点了别的 Tab，接着切到最后一次请求的那个。 */
  TabBackgroundStory.prototype.finishTransition = function () {
    var pendingId = this.pendingId;
    this.transitioning = false;
    this.root.setAttribute('data-transitioning', 'false');
    this.pendingId = null;
    if (pendingId && pendingId !== this.activeId) {
      this.selectTab(pendingId);
    }
  };

  /** 返回 Tab 按钮在列表中的下标，供键盘左右切换使用。 */
  TabBackgroundStory.prototype.getTabIndex = function (tab) {
    return this.tabs.indexOf(tab);
  };

  /**
   * 按偏移量聚焦并激活相邻 Tab（到头循环）。
   * @param {HTMLElement} currentTab
   * @param {number} offset 1 为右，-1 为左
   */
  TabBackgroundStory.prototype.focusTabByOffset = function (currentTab, offset) {
    var index = this.getTabIndex(currentTab);
    if (index < 0) return;
    var nextIndex = (index + offset + this.tabs.length) % this.tabs.length;
    var nextTab = this.tabs[nextIndex];
    if (!nextTab) return;
    nextTab.focus();
    this.selectTab(nextTab.getAttribute('data-tab-target'));
  };

  /** 绑定点击、悬停预加载、键盘左右/Home/End 切换。 */
  TabBackgroundStory.prototype.bind = function () {
    var self = this;

    this.bound.onTabClick = function (event) {
      var tab = event.currentTarget;
      var nextId = tab.getAttribute('data-tab-target');
      self.selectTab(nextId);
    };

    this.bound.onTabFocus = function (event) {
      var tab = event.currentTarget;
      self.preloadLayer(self.getLayer(tab.getAttribute('data-tab-target')), false);
    };

    this.bound.onKeydown = function (event) {
      var tab = event.target.closest('[data-tab-target]');
      if (!tab || !self.root.contains(tab)) return;

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        self.focusTabByOffset(tab, 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        self.focusTabByOffset(tab, -1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        self.tabs[0].focus();
        self.selectTab(self.tabs[0].getAttribute('data-tab-target'));
      } else if (event.key === 'End') {
        event.preventDefault();
        var last = self.tabs[self.tabs.length - 1];
        last.focus();
        self.selectTab(last.getAttribute('data-tab-target'));
      }
    };

    this.tabs.forEach(function (tab) {
      tab.addEventListener('click', self.bound.onTabClick);
      tab.addEventListener('mouseenter', self.bound.onTabFocus);
      tab.addEventListener('focus', self.bound.onTabFocus);
    });

    this.root.addEventListener('keydown', this.bound.onKeydown);
  };

  /** 解绑事件并重置切换锁，供 Theme Editor 卸载/重新加载 section 时调用。 */
  TabBackgroundStory.prototype.destroy = function () {
    var self = this;
    this.tabs.forEach(function (tab) {
      tab.removeEventListener('click', self.bound.onTabClick);
      tab.removeEventListener('mouseenter', self.bound.onTabFocus);
      tab.removeEventListener('focus', self.bound.onTabFocus);
    });

    this.root.removeEventListener('keydown', this.bound.onKeydown);
    this.transitioning = false;
    this.pendingId = null;
    this.root.setAttribute('data-transitioning', 'false');
  };

  /**
   * Theme Editor 选中某个 block 时，根据面板或 Tab 节点切到对应内容。
   * @param {HTMLElement} blockEl
   * @param {boolean} instant
   */
  TabBackgroundStory.prototype.selectBlockEl = function (blockEl, instant) {
    if (!blockEl) return;
    var panel = blockEl.closest('[data-panel]') || blockEl.querySelector('[data-panel]');
    var nextId = panel ? panel.getAttribute('data-panel') : '';
    if (!nextId) {
      nextId = blockEl.getAttribute('data-parent-tab') || '';
    }
    if (!nextId) {
      var tab = blockEl.closest('[data-tab-target]') || blockEl.querySelector('[data-tab-target]');
      nextId = tab ? tab.getAttribute('data-tab-target') : '';
    }
    if (nextId) this.selectTab(nextId, { instant: instant });
  };

  /** 初始化或重建单个根节点上的实例。 */
  function initRoot(root) {
    if (!root) return;
    if (root._tabBackgroundStory) {
      root._tabBackgroundStory.destroy();
      root._tabBackgroundStory = null;
    }
    root._tabBackgroundStory = new TabBackgroundStory(root);
  }

  /** 在给定范围内找出所有 Tab Story section 并初始化。 */
  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i += 1) {
      initRoot(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var node = event.target.querySelector(SELECTOR);
    if (node && node._tabBackgroundStory) {
      node._tabBackgroundStory.destroy();
      node._tabBackgroundStory = null;
    }
  });

  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest(SELECTOR) || event.target.querySelector(SELECTOR);
    if (!root || !root._tabBackgroundStory) return;
    root._tabBackgroundStory.selectBlockEl(event.target, true);
  });
})();
